import React, { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Printer, XCircle, AlertTriangle, Info, CheckCircle2 } from 'lucide-react';
import { apiUrl } from '../../config/api';
import { AuthContext } from '../../context/AuthContext';
import { formatDateDMY, formatDateTimeDMY } from '../../utils/dateUtils';
import { toCurrency } from '../../utils/adminMetrics';
import { TABLE_HEAD_ROW, TABLE_HEAD_TEXT, TABLE_ROW_LINE } from '../UI/tableSort';
import CollapsibleRow from '../UI/CollapsibleRow';
import {
  SEVERITY_TAGS, SOURCE_LABELS, decisionView, recordView, isPreQualified, sortReasons, bannerSentence,
  hasOverridePermission, qualificationRows, riskRatingView,
} from './affordabilityView';

// The loan's affordability check (NAMFISA ML.S.4; Microlending Act ss
// 23(1)(b), 24(4)), from GET /api/loans/:id/affordability: the decision in
// force, every reason, the qualification figures, what the documents showed
// and the officer overrides. null while loading or when the check was never
// saved on the loan. reloadKey: fetched again when it changes.
export const useLoanAffordability = (loanId, reloadKey = '') => {
  const [record, setRecord] = useState(null);
  useEffect(() => {
    setRecord(null);
    if (!loanId) return undefined;
    let cancelled = false;
    axios.get(apiUrl(`/api/loans/${loanId}/affordability`), { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then((res) => { if (!cancelled) setRecord(res.data || null); })
      .catch(() => { if (!cancelled) setRecord(null); });
    return () => { cancelled = true; };
  }, [loanId, reloadKey]);
  return record;
};

const GOOD = 'text-emerald-700 dark:text-emerald-400';
const BAD = 'text-rose-600 dark:text-rose-300';
const cell = 'px-2 py-1.5';
const monthName = (ym) => {
  const m = /^(\d{4})-(\d{2})$/.exec(String(ym || ''));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, 1).toLocaleString('en-GB', { month: 'long', year: 'numeric' }) : String(ym || '');
};
const money = (v) => (v === null || v === undefined ? '—' : toCurrency(v));

const Table = ({ head, children }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-xs border-collapse">
      <thead>
        <tr className={TABLE_HEAD_ROW}>
          {head.map((h) => <th key={h.label} scope="col" className={`${cell} ${TABLE_HEAD_TEXT} ${h.right ? 'text-right' : 'text-left'}`}>{h.label}</th>)}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);
const Empty = ({ cols, children }) => (
  <tr className={TABLE_ROW_LINE}><td colSpan={cols} className={`${cell} text-gray-500 dark:text-slate-400`}>{children}</td></tr>
);

// The reasons by how serious they are: what declined it, what an officer
// must review, and notes. edge: the coloured side each reason has instead
// of its icon (the edge look: red, yellow, green).
const REASON_GROUPS = [
  { severity: 'Decline', title: 'Why it was declined', Icon: XCircle, icon: 'text-rose-600 dark:text-rose-400', edge: 'border-l-rose-500 dark:border-l-rose-500', head: 'bg-rose-50/70 dark:bg-rose-900/15 text-rose-900 dark:text-rose-200 border-rose-200 dark:border-rose-800/60' },
  { severity: 'Review', title: 'For an officer to review', Icon: AlertTriangle, icon: 'text-amber-600 dark:text-amber-400', edge: 'border-l-amber-400 dark:border-l-amber-500', head: 'bg-amber-50/70 dark:bg-amber-900/15 text-amber-900 dark:text-amber-200 border-amber-200 dark:border-amber-800/60' },
  { severity: 'Info', title: 'Notes', Icon: Info, icon: 'text-slate-500 dark:text-slate-400', edge: 'border-l-emerald-500 dark:border-l-emerald-500', head: 'bg-slate-50 dark:bg-slate-800/60 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700', edgeHead: 'bg-emerald-50/70 dark:bg-emerald-900/15 text-emerald-900 dark:text-emerald-200 border-emerald-200 dark:border-emerald-800/60' },
];

// A long detail ("A: x, y, z. B: ...") as its sentences, one per line, so a
// list of transactions reads as a list.
const detailLines = (detail) => String(detail || '').split(/(?<=\.)\s+(?=[A-Z])/).filter(Boolean);

// The statement rows a reason is about, as they are on the statement: name,
// date and amount (money in marked when the rows mix in and out).
export const ReasonTransactions = ({ rows }) => {
  if (!Array.isArray(rows) || !rows.length) return null;
  const mixed = rows.some((t) => t.direction === 'credit') && rows.some((t) => t.direction !== 'credit');
  return (
    <div className="mt-1.5 overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className={TABLE_HEAD_ROW}>
            <th scope="col" className={`${cell} ${TABLE_HEAD_TEXT} text-left`}>Name</th>
            <th scope="col" className={`${cell} ${TABLE_HEAD_TEXT} text-left whitespace-nowrap`}>Date</th>
            {mixed && <th scope="col" className={`${cell} ${TABLE_HEAD_TEXT} text-left`}>In / out</th>}
            <th scope="col" className={`${cell} ${TABLE_HEAD_TEXT} text-right`}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t, i) => (
            <tr key={`${t.date}-${i}`} className={TABLE_ROW_LINE}>
              <td className={`${cell} text-gray-800 dark:text-slate-200 break-words`}>{t.description}</td>
              <td className={`${cell} text-gray-600 dark:text-slate-300 whitespace-nowrap`}>{formatDateDMY(t.date)}</td>
              {mixed && <td className={`${cell} ${t.direction === 'credit' ? GOOD : 'text-gray-600 dark:text-slate-300'}`}>{t.direction === 'credit' ? 'In' : 'Out'}</td>}
              <td className={`${cell} text-right tabular-nums whitespace-nowrap text-gray-800 dark:text-slate-200`}>{toCurrency(t.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// One reason: an icon (or, with edge, a coloured side), its title, where
// it was found, the detail, and the statement rows behind it.
const ReasonItem = ({ reason, Icon, iconClass, edge = null }) => {
  const lines = detailLines(reason.detail);
  return (
    <li className={`flex gap-3 px-3 py-2.5 border-t border-gray-100 dark:border-slate-800 first:border-t-0 ${edge ? `border-l-4 ${edge}` : ''}`}>
      {!edge && <Icon size={16} className={`mt-0.5 shrink-0 ${iconClass}`} aria-hidden="true" />}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
          <span className="text-sm font-semibold text-gray-900 dark:text-slate-100">{reason.title}</span>
          <span className="text-[11px] text-gray-500 dark:text-slate-400 whitespace-nowrap">{SOURCE_LABELS[reason.source] || reason.source}</span>
        </div>
        {lines.length > 2 || (lines.length > 1 && String(reason.detail).length > 180) ? (
          <ul className="mt-1 space-y-0.5 text-xs leading-relaxed text-gray-600 dark:text-slate-300 list-disc pl-4">
            {lines.map((line, i) => <li key={i} className="break-words">{line}</li>)}
          </ul>
        ) : (
          <p className="mt-0.5 text-xs leading-relaxed text-gray-600 dark:text-slate-300 break-words">{reason.detail}</p>
        )}
        <ReasonTransactions rows={reason.transactions} />
        {/* The amount the client does qualify for (asked for too much). */}
        {reason.suggestion?.amount > 0 && (
          <p className="mt-1.5 inline-flex flex-wrap items-center gap-x-1.5 px-2 py-1 border border-emerald-200 dark:border-emerald-800/60 bg-emerald-50/70 dark:bg-emerald-900/15 text-xs text-emerald-900 dark:text-emerald-200">
            <CheckCircle2 size={13} aria-hidden="true" className="text-emerald-600 dark:text-emerald-400" />
            <span>
              Suggested amount: <strong>{toCurrency(reason.suggestion.amount)}</strong> over {reason.suggestion.months} month{reason.suggestion.months === 1 ? '' : 's'},
              repaid at <strong>{toCurrency(reason.suggestion.instalment)}</strong> a month (within the recommended instalment).
            </span>
          </p>
        )}
      </div>
    </li>
  );
};

// The reasons, a box for each severity, declines first. look: 'icon' (the
// staff screen) or 'edge' (each reason's side red, yellow or green, as the
// pre-qualification screen shows them); titles: the boxes' headings.
// collapsible: each box is a row that opens on a click (the staff panel;
// the declines open to start with); forceOpen: all open (printing).
export const ReasonGroups = ({ reasons, look = 'icon', titles = {}, collapsible = false, forceOpen = false }) => (
  <div className={collapsible ? '' : 'mt-3 space-y-3'}>
    {REASON_GROUPS.map((g) => {
      const list = reasons.filter((r) => (r.severity || 'Info') === g.severity);
      if (!list.length) return null;
      const tag = SEVERITY_TAGS[g.severity];
      const edged = look === 'edge';
      if (collapsible) {
        return (
          <CollapsibleRow key={g.severity} title={titles[g.severity] || g.title} summary={`${list.length} ${list.length === 1 ? 'reason' : 'reasons'}`}
            tone={g.severity === 'Decline' ? BAD : g.severity === 'Review' ? 'text-amber-700 dark:text-amber-300' : ''}
            defaultOpen={g.severity === 'Decline'} forceOpen={forceOpen}>
            <ul className="border border-gray-200 dark:border-slate-700">
              {list.map((r, i) => <ReasonItem key={`${r.code}-${i}`} reason={r} Icon={g.Icon} iconClass={g.icon} edge={edged ? g.edge : null} />)}
            </ul>
          </CollapsibleRow>
        );
      }
      return (
        <section key={g.severity} className="border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900">
          <header className={`flex items-center gap-2 px-3 py-1.5 border-b text-xs font-semibold ${edged && g.edgeHead ? g.edgeHead : g.head}`}>
            {!edged && <g.Icon size={14} aria-hidden="true" className={g.icon} />}
            {titles[g.severity] || g.title}
            <span className={`ml-auto px-1.5 py-0.5 border text-[10px] ${tag.className}`}>{list.length}</span>
          </header>
          <ul>{list.map((r, i) => <ReasonItem key={`${r.code}-${i}`} reason={r} Icon={g.Icon} iconClass={g.icon} edge={edged ? g.edge : null} />)}</ul>
        </section>
      );
    })}
  </div>
);

// The qualification figures in ML.S.4 order: the binding cap marked, the
// proposed instalment in red when it is above a maximum.
const QualificationTable = ({ q }) => (
  <Table head={[{ label: 'Item' }, { label: 'A month', right: true }]}>
    {qualificationRows(q).map((row) => (
      <tr
        key={row.key}
        className={`${TABLE_ROW_LINE} ${row.highlight === 'binding' ? 'bg-indigo-50/70 dark:bg-indigo-900/20' : ''} ${row.highlight === 'over' ? 'bg-rose-50/70 dark:bg-rose-900/15' : ''}`}
      >
        <td className={`${cell} ${row.strong ? 'font-semibold' : ''}`}>
          {row.label}
          {row.highlight === 'binding' && <span className="ml-2 text-[10px] font-semibold text-indigo-700 dark:text-indigo-300">Binding cap</span>}
          {row.highlight === 'over' && <span className={`ml-2 text-[10px] font-semibold ${BAD}`}>Above the maximum</span>}
        </td>
        <td className={`${cell} text-right tabular-nums whitespace-nowrap ${row.strong ? 'font-semibold' : ''} ${row.highlight === 'over' ? `${BAD} font-semibold` : ''}`}>
          {row.value === null || row.value === undefined ? '—' : `${row.sign === '-' ? '− ' : ''}${toCurrency(row.value)}`}
        </td>
      </tr>
    ))}
  </Table>
);

// Koniza's internal risk rating at a glance, as the score strip showed it:
// the band and score in a box of the band's colour, then the risk level,
// what it offers and the longest term. Always in view.
export const RiskRatingStrip = ({ rating }) => {
  const v = riskRatingView(rating);
  if (!v) return null;
  const field = (label, value, tone = '') => (
    <div className="min-w-0">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">{label}</p>
      <p className={`text-sm font-semibold text-gray-900 dark:text-slate-100 ${tone}`}>{value}</p>
    </div>
  );
  return (
    <div className="mt-3 flex flex-wrap items-center gap-4 bg-gray-50 dark:bg-slate-800/60 border border-gray-200 dark:border-slate-700 px-3 py-2.5">
      <div className={`flex items-center gap-2.5 border px-3 py-1.5 ${v.badge}`}>
        <span className="text-2xl font-bold leading-none">{v.band}</span>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide opacity-80">Score</p>
          <p className="text-base font-bold leading-tight tabular-nums">{v.score}<span className="text-xs font-semibold opacity-70"> / {v.total_max}</span></p>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 flex-1 min-w-[16rem]">
        {field('Internal risk rating', `Band ${v.band}${v.label ? `: ${v.label}` : ''}`)}
        {field('Risk level', v.risk_level || '—')}
        {field('Offers', !v.enabled ? 'For information only' : v.share_pct === null ? '—' : v.share_pct > 0 ? `${v.share_pct}% of the recommended` : 'Nothing', v.enabled && v.share_pct === 0 ? BAD : '')}
        {field('Longest term', !v.enabled || v.max_term === null ? '—' : v.max_term > 0 ? `${v.max_term} months${v.first_loan_cap ? `, first loan up to ${toCurrency(v.first_loan_cap)}` : ''}` : 'None', v.enabled && v.max_term === 0 ? BAD : '')}
      </div>
    </div>
  );
};

// How the rating was scored: the points on each of the six factors with
// what was found (a row that opens on a click).
export const RiskRatingPanel = ({ rating, forceOpen = false }) => {
  const v = riskRatingView(rating);
  if (!v) return null;
  return (
    <CollapsibleRow title="Internal risk rating: how it was scored" summary={`Band ${v.band}${v.label ? `: ${v.label}` : ''} · ${v.score} / ${v.total_max}`}
      tone={['D', 'E'].includes(v.band) ? BAD : v.band === 'A' ? GOOD : ''} forceOpen={forceOpen}>
      <p className="text-xs text-gray-700 dark:text-slate-300">{v.effect}</p>
      <p className="mt-0.5 mb-2 text-[11px] text-gray-500 dark:text-slate-400">Koniza's own credit policy on top of the NAMFISA test: it only lowers the offer, never the statutory maximum. Only financial behaviour is scored.</p>
      <Table head={[{ label: 'Factor' }, { label: 'Points', right: true }, { label: 'What was found' }]}>
        {v.factors.map((f) => (
          <tr key={f.key} className={TABLE_ROW_LINE}>
            <td className={`${cell} font-medium whitespace-nowrap`}>{f.label}</td>
            <td className={`${cell} text-right tabular-nums whitespace-nowrap ${f.full ? GOOD : f.none ? BAD : ''}`}>{f.points} / {f.max}</td>
            <td className={`${cell} text-gray-600 dark:text-slate-300`}>{f.detail}</td>
          </tr>
        ))}
      </Table>
    </CollapsibleRow>
  );
};

// The loan amount each term supports at the recommended (and the statutory)
// maximum instalment, the full instalment kept within it.
const SupportedAmounts = ({ q, loanMonths, forceOpen = false }) => {
  const rows = Array.isArray(q?.supported_amounts) ? q.supported_amounts : [];
  if (!rows.length) return null;
  const onTerm = rows.find((r) => Number(r.months) === Number(loanMonths));
  const best = rows.reduce((m, r) => Math.max(m, Number(r.recommended_principal) || 0), 0);
  return (
    <CollapsibleRow title="Loan amounts per term" forceOpen={forceOpen}
      summary={onTerm ? `${onTerm.months} months: ${toCurrency(onTerm.recommended_principal)}` : `Up to ${toCurrency(best)}`}
      tone={(onTerm ? Number(onTerm.recommended_principal) : best) > 0 ? GOOD : BAD}>
      <p className="mb-1 text-xs text-gray-600 dark:text-slate-300">The loan amount each term supports at the recommended (and the statutory) maximum instalment.</p>
      <Table head={[{ label: 'Term' }, { label: 'Recommended', right: true }, { label: 'Statutory maximum', right: true }]}>
        {rows.map((r) => (
          <tr key={r.months} className={`${TABLE_ROW_LINE} ${Number(r.months) === Number(loanMonths) ? 'bg-indigo-50/70 dark:bg-indigo-900/20 font-semibold' : ''}`}>
            <td className={cell}>{r.months} month{Number(r.months) === 1 ? '' : 's'}{Number(r.months) === Number(loanMonths) ? ' (this loan)' : ''}</td>
            <td className={`${cell} text-right tabular-nums`}>{toCurrency(r.recommended_principal)}</td>
            <td className={`${cell} text-right tabular-nums`}>{toCurrency(r.statutory_principal)}</td>
          </tr>
        ))}
      </Table>
    </CollapsibleRow>
  );
};

// What the documents showed: each check's figures (collapsed by default).
const VerificationDetails = ({ record, forceOpen }) => {
  const v = record.verification || {};
  const salary = Array.isArray(v.salary_match) ? v.salary_match : [];
  const unmatched = Array.isArray(v.unmatched_recurring_debits) ? v.unmatched_recurring_debits : [];
  const returned = v.returned_debit_orders || { count: 0, entries: [] };
  const overdraft = v.overdraft || {};
  const stress = Array.isArray(v.stress) ? v.stress : [];
  const declinedTx = Array.isArray(v.declined_transactions) ? v.declined_transactions : [];
  const declared = Array.isArray(v.declared_vs_observed) ? v.declared_vs_observed : [];
  const pass = Array.isArray(v.pass_through) ? v.pass_through : [];
  const own = Array.isArray(v.own_account_transfers) ? v.own_account_transfers : [];
  const microloans = Array.isArray(v.microloans) ? v.microloans : [];
  const salaryBad = salary.some((s) => s.matches === false);
  // The month checked against the payslip; earlier months are compared with it.
  const latestSalary = salary.find((s) => s.compared_to === 'payslip') || null;
  return (
    <CollapsibleRow title="Verification details" summary={salaryBad ? 'Salary does not match' : `${salary.length} months checked`} tone={salaryBad ? BAD : GOOD} forceOpen={forceOpen}>
      <div className="space-y-4">
        <div>
          <p className="mb-1 text-xs font-semibold text-gray-700 dark:text-slate-200">Salary each month: the latest against the payslip, earlier months against the latest</p>
          <Table head={[{ label: 'Month' }, { label: 'Paid in' }, { label: 'Salary credit', right: true }, { label: 'Compared with', right: true }, { label: 'Result' }]}>
            {salary.length ? salary.map((s) => (
              <tr key={s.month} className={TABLE_ROW_LINE}>
                <td className={cell}>{monthName(s.month)}</td>
                <td className={cell}>{s.date ? formatDateDMY(s.date) : '—'}</td>
                <td className={`${cell} text-right tabular-nums`}>{money(s.amount)}</td>
                <td className={`${cell} text-right tabular-nums`}>
                  {s.compared_to === 'latest_salary' && latestSalary
                    ? <>{money(latestSalary.amount)} <span className="text-gray-500 dark:text-slate-400">latest salary</span></>
                    : <>{money(s.payslip_net)} <span className="text-gray-500 dark:text-slate-400">payslip net</span></>}
                </td>
                <td className={`${cell} ${s.matches === false ? BAD : s.matches ? GOOD : 'text-gray-500 dark:text-slate-400'}`}>{s.note}</td>
              </tr>
            )) : <Empty cols={5}>No salary months were checked.</Empty>}
          </Table>
        </div>

        <div>
          <p className="mb-1 text-xs font-semibold text-gray-700 dark:text-slate-200">Debit orders not on the bureau report, the payslip or the declaration</p>
          <Table head={[{ label: 'Payee' }, { label: 'Around day' }, { label: 'Months' }, { label: 'A month', right: true }, { label: 'Counted as' }]}>
            {unmatched.length ? unmatched.map((u) => (
              <tr key={`${u.payee}-${u.usual_day}`} className={TABLE_ROW_LINE}>
                <td className={`${cell} break-all`}>{u.payee}</td>
                <td className={cell}>{u.usual_day}</td>
                <td className={cell}>{u.months}</td>
                <td className={`${cell} text-right tabular-nums`}>{toCurrency(u.monthly)}</td>
                <td className={cell}>{u.added_to === 'CreditObligations' ? 'Credit obligation' : 'Other commitment'}</td>
              </tr>
            )) : <Empty cols={5}>None found.</Empty>}
          </Table>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="mb-1 text-xs font-semibold text-gray-700 dark:text-slate-200">Returned debit orders: {returned.count || 0}</p>
            <ul className="text-xs text-gray-700 dark:text-slate-300 space-y-0.5">
              {(returned.entries || []).map((t, i) => <li key={i}>{formatDateDMY(t.date)} · {t.description} · {toCurrency(t.amount)}</li>)}
              {!(returned.entries || []).length && <li className="text-gray-500 dark:text-slate-400">None in the last three months.</li>}
            </ul>
          </div>
          <div>
            <p className="mb-1 text-xs font-semibold text-gray-700 dark:text-slate-200">
              Overdraft{overdraft.limit ? ` (limit ${toCurrency(overdraft.limit)})` : ' (limit not shown on the statement)'}
            </p>
            <ul className="text-xs text-gray-700 dark:text-slate-300 space-y-0.5">
              {(overdraft.excess_entries || []).map((t, i) => <li key={`x${i}`} className={BAD}>Excess: {formatDateDMY(t.date)} · {t.description} · {toCurrency(t.amount)}</li>)}
              {(overdraft.months || []).map((m) => (
                <li key={m.month}>{monthName(m.month)}: lowest balance {money(m.lowest_balance)}{m.near_limit ? ' (near the limit)' : ''}</li>
              ))}
              {overdraft.amortised_monthly ? <li>Counted as an obligation: {toCurrency(overdraft.amortised_monthly)} a month</li> : null}
              {overdraft.balances_available === false && <li className="text-gray-500 dark:text-slate-400">The statement's balances could not be read.</li>}
            </ul>
          </div>
        </div>

        <div>
          <p className="mb-1 text-xs font-semibold text-gray-700 dark:text-slate-200">Cash flow after each salary</p>
          <Table head={[{ label: 'Month' }, { label: 'Salary' }, { label: 'Lowest balance', right: true }, { label: 'Days at the floor', right: true }, { label: 'Reached it after', right: true }]}>
            {stress.length ? stress.map((s) => (
              <tr key={s.month} className={`${TABLE_ROW_LINE} ${s.stressed ? 'bg-rose-50/70 dark:bg-rose-900/15' : ''}`}>
                <td className={cell}>{monthName(s.month)}</td>
                <td className={cell}>{s.salary_date ? formatDateDMY(s.salary_date) : '—'}</td>
                <td className={`${cell} text-right tabular-nums`}>{money(s.lowest_balance)}</td>
                <td className={`${cell} text-right tabular-nums`}>{s.days_at_or_below_floor}</td>
                <td className={`${cell} text-right tabular-nums`}>{s.reached_floor_after_days === null ? '—' : `${s.reached_floor_after_days} day${s.reached_floor_after_days === 1 ? '' : 's'}`}</td>
              </tr>
            )) : <Empty cols={5}>The statement's balances could not be read.</Empty>}
          </Table>
          {declinedTx.some((d) => d.count > 0) && (
            <p className="mt-1 text-xs text-gray-700 dark:text-slate-300">Declined transactions: {declinedTx.map((d) => `${monthName(d.month)} ${d.count}`).join(', ')}</p>
          )}
        </div>

        <div>
          <p className="mb-1 text-xs font-semibold text-gray-700 dark:text-slate-200">Living costs: declared and seen on the statements (the higher is used)</p>
          <Table head={[{ label: 'Item' }, { label: 'Declared', right: true }, { label: 'Seen', right: true }, { label: 'Used', right: true }]}>
            {declared.length ? declared.map((d) => (
              <tr key={d.item} className={TABLE_ROW_LINE}>
                <td className={cell}>{d.item}</td>
                <td className={`${cell} text-right tabular-nums`}>{money(d.declared)}</td>
                <td className={`${cell} text-right tabular-nums`}>{money(d.observed)}</td>
                <td className={`${cell} text-right tabular-nums font-semibold`}>{money(d.used)}</td>
              </tr>
            )) : <Empty cols={4}>No living costs declared or seen.</Empty>}
          </Table>
        </div>

        {(microloans.length > 0 || pass.length > 0 || own.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {microloans.length > 0 && (
              <div>
                <p className="mb-1 text-xs font-semibold text-gray-700 dark:text-slate-200">Active microloans: {microloans.length}</p>
                <ul className="text-xs text-gray-700 dark:text-slate-300 space-y-0.5">
                  {microloans.map((m) => <li key={m.lender}>{m.lender} ({m.source}){m.monthly ? `: ${toCurrency(m.monthly)} a month` : ''}</li>)}
                </ul>
              </div>
            )}
            {(pass.length > 0 || own.length > 0) && (
              <div>
                <p className="mb-1 text-xs font-semibold text-gray-700 dark:text-slate-200">Money passed through, and own-account transfers</p>
                <ul className="text-xs text-gray-700 dark:text-slate-300 space-y-0.5">
                  {pass.map((p, i) => <li key={`p${i}`}>{formatDateDMY(p.in.date)} · {p.in.description} {toCurrency(p.in.amount)} in, out to {p.out.description}</li>)}
                  {own.map((t, i) => <li key={`o${i}`}>{formatDateDMY(t.date)} · {t.description} · {toCurrency(t.amount)}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </CollapsibleRow>
  );
};

// Who overrode the decision, when, from what to what, and why.
const OverrideRecord = ({ overrides, systemDecision }) => {
  if (!overrides?.length) return null;
  return (
    <div className="mt-3 border border-indigo-200 dark:border-indigo-700/60 bg-indigo-50/50 dark:bg-slate-800 px-3 py-2">
      <p className="text-xs font-semibold text-indigo-900 dark:text-indigo-100">
        Overridden by an officer (the check's own decision: {decisionView(systemDecision)?.label || systemDecision})
      </p>
      <ul className="mt-1 space-y-1">
        {overrides.map((o) => (
          <li key={o.id} className="text-xs text-indigo-900/90 dark:text-indigo-200">
            {formatDateTimeDMY(o.created_at)} · {o.officer_email || `User #${o.officer_id}`} changed it from {decisionView(o.original_decision)?.label} to <strong>{decisionView(o.new_decision)?.label}</strong>: “{o.reason}”
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * The Affordability section: the decision banner, any override, the reasons
 * in severity order, the qualification table and the amounts each term
 * supports, and the verification details (collapsed). A declined or
 * referred loan is approved from the header's Approve, with the reason it is
 * approved (recorded as the override).
 *   record        from useLoanAffordability
 *   loanId        the loan
 *   loanMonths    its term, marked in the amounts table
 *   allowOverride false where the decision is only shown (editing a loan)
 *   onChanged     after an override (to reload)
 */
const AffordabilityCheck = ({ record, loanId, loanMonths = null, allowOverride = true, onChanged }) => {
  const { user } = useContext(AuthContext) || {};
  const perms = user?.permissions || [];
  const [printing, setPrinting] = useState(false);
  const sectionRef = useRef(null);
  const view = recordView(record);

  // Print the section on its own (for a NAMFISA inspection): once the
  // details are open, it is copied with the app's styles into a window of
  // its own and printed there, light, whatever the screen shows.
  useEffect(() => {
    if (!printing) return undefined;
    const t = setTimeout(() => {
      const html = sectionRef.current?.outerHTML || '';
      const styles = [...document.querySelectorAll('link[rel="stylesheet"], style')].map((n) => n.outerHTML).join('\n');
      const w = window.open('', '_blank', 'width=900,height=1000');
      if (w) {
        w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Affordability check</title>${styles}
          <style>body{background:#fff;padding:24px;font-family:Inter,Arial,sans-serif}.print\\:hidden{display:none!important}</style></head>
          <body>${html}</body></html>`);
        w.document.close();
        w.focus();
        setTimeout(() => { w.print(); }, 300);
      }
      setPrinting(false);
    }, 50);
    return () => clearTimeout(t);
  }, [printing]);

  if (!record || !view) return null;
  const reasons = sortReasons(record.reasons);
  const preQualified = isPreQualified(record);

  return (
    <div ref={sectionRef}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`px-2 py-0.5 border text-[11px] font-semibold ${view.style.badge}`}>{view.label}</span>
        <p className="text-sm font-semibold text-gray-900 dark:text-slate-100">{bannerSentence(record)}</p>
        <span className="ml-auto flex items-center gap-2 text-[11px] text-gray-500 dark:text-slate-400">
          Affordability check {formatDateDMY(record.checked_at)}
          <button type="button" onClick={() => setPrinting(true)} title="Print the affordability check" aria-label="Print the affordability check"
            className="p-1 text-gray-500 hover:text-gray-800 dark:hover:text-slate-200 print:hidden">
            <Printer size={14} />
          </button>
        </span>
      </div>
      {record.system_decision !== record.decision && (
        <p className="mt-1 text-xs text-gray-600 dark:text-slate-400">The check's own decision was {decisionView(record.system_decision)?.label}.</p>
      )}
      <OverrideRecord overrides={record.overrides} systemDecision={record.system_decision} />

      {/* Koniza's internal risk rating, in view at the top. */}
      <RiskRatingStrip rating={record.risk_rating} />

      {/* Every part a row that opens on a click (all open to print). */}
      <div className="mt-3 border-t border-gray-200 dark:border-slate-700">
        {reasons.length > 0 && <ReasonGroups reasons={reasons} collapsible forceOpen={printing} />}
        <RiskRatingPanel rating={record.risk_rating} forceOpen={printing} />
        {record.qualification && (
          <CollapsibleRow title="Qualification (NAMFISA ML.S.4)" forceOpen={printing}
            summary={`Statutory ${toCurrency(record.qualification.statutory_max_instalment)} · recommended ${toCurrency(record.qualification.recommended_max_instalment)} a month`}
            tone={Number(record.qualification.statutory_max_instalment) > 0 ? GOOD : BAD}>
            <QualificationTable q={record.qualification} />
          </CollapsibleRow>
        )}
        <SupportedAmounts q={record.qualification} loanMonths={loanMonths} forceOpen={printing} />
        <VerificationDetails record={record} forceOpen={printing} />
      </div>

      {preQualified && (
        <p className="mt-3 text-xs text-indigo-800 dark:text-indigo-300 print:hidden">
          This is a pre-qualification: capture the credit bureau report on the application, then run the calculation again.
          {hasOverridePermission(perms) ? ' To approve it before that, use Approve at the top: it asks for your reason, which is kept with the check.' : ' An officer with the override permission can approve it before that, giving the reason.'}
        </p>
      )}
      {allowOverride && !preQualified && (record.decision === 'Declined' || record.decision === 'ManualReview') && (
        <p className="mt-3 text-xs text-gray-500 dark:text-slate-400 print:hidden">
          {hasOverridePermission(perms)
            ? 'To go ahead, use Approve at the top: it asks for the reason you approve it, which is kept with the check.'
            : 'An officer with the override permission must approve this, giving the reason.'}
        </p>
      )}
    </div>
  );
};

export default AffordabilityCheck;
